import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleBackdropConfig, handleSnackBar } from "./global";
import MESSAGES from "../constants/messages";
// import { postAPICall } from '../utils/axios.util'
// import { nextAPIRoutes } from '../constants/apiRoutes'
import { clearCartCache } from "../utils/cart.util";
import cartService from "../services/cart.service";
import insuranceService from "../services/insurance.service";

export const addItemToCart = createAsyncThunk(
  "cart/add",
  async (
    { id, deliveryType = 0 }: { id: number; deliveryType?: number },
    { dispatch }
  ) => {
    try {
      dispatch(setAddingToCartId(id));
      const resp: any = await cartService.addItem({
        body: {
          listing_id: id,
          delivery_type: deliveryType,
        },
      });
      // postAPICall(nextAPIRoutes.ADD_ITEM_TO_CART, {
      //   listing_id: id,
      //   delivery_type: deliveryType,
      // })
      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: "Equipment added successfully.",
        })
      );
      dispatch(setAddingToCartId(0));
      dispatch(fetchCart({ query: "" }));
      return resp.data.data ?? [];
    } catch (e: any) {
      dispatch(setAddingToCartId(0));
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e?.response?.data?.message ?? MESSAGES.ERROR,
        })
      );
      return [];
    }
  }
);

export const fetchCart = createAsyncThunk(
  "cart/fetch",
  async (
    {
      query,
      softReload = false,
      clearIfNoItem = false,
    }: { query?: string; softReload?: boolean; clearIfNoItem?: boolean },
    { dispatch }
  ) => {
    try {
      if (!softReload) {
        dispatch(setLoadingCart(true));
      }
      const resp: any = await cartService.fetch({ body: { query } }); //postAPICall(nextAPIRoutes.FETCH_CART, { query })

      dispatch(setLoadingCart(false));
      dispatch(setIsDeleting(false));

      if (resp?.data?.data?.items?.length === 0 && clearIfNoItem) {
        clearCartCache();
      }
      return resp?.data?.data ?? null;
    } catch (e: any) {
      dispatch(setLoadingCart(false));
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e?.response?.data?.message ?? MESSAGES.ERROR,
        })
      );
      return { items: [] };
    }
  }
);

export const fetchInsurance = createAsyncThunk(
  "cart/fetchInsurance",
  async (_, { dispatch }) => {
    try {
      const resp: any = await insuranceService.fetch({ body: {} }); //postAPICall(nextAPIRoutes.FETCH_INSURANCE, {})
      return resp?.data?.data ?? [];
    } catch (e: any) {
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e?.response?.data?.message ?? MESSAGES.ERROR,
        })
      );
      return { items: [] };
    }
  }
);

export const updateDeliveryType = createAsyncThunk(
  "cart/update",
  async (data: any, { dispatch }) => {
    try {
      const resp: any = await cartService.updateItem({ body: data }); //postAPICall(nextAPIRoutes.UPDATE_CART_ITEM, data)
      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: "Cart item updated successfully.",
        })
      );
      dispatch(fetchCart({ query: "" }));
      return resp.data.data ?? [];
    } catch (e: any) {
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e?.response?.data?.message ?? MESSAGES.ERROR,
        })
      );
      return [];
    }
  }
);

export const removeCartItem = createAsyncThunk(
  "cart/remove",
  async ({ id, onSuccess }: { id: any; onSuccess: any }, { dispatch }) => {
    try {
      dispatch(setIsDeleting(true));
      const resp: any = await cartService.remove({
        body: {
          id,
        },
      });
      // postAPICall(nextAPIRoutes.REMOVE_CART_ITEM, {
      //   id,
      // })
      dispatch(
        handleSnackBar({
          open: true,
          severity: "success",
          message: "Cart item deleted successfully.",
        })
      );

      onSuccess();
      dispatch(setLoadingCart(false));

      return resp.data.data ?? [];
    } catch (e: any) {
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e?.response?.data?.message ?? MESSAGES.ERROR,
        })
      );
      return [];
    }
  }
);

export const updateCartDelvType = createAsyncThunk(
  "cart/updateCartDelvType",
  async ({ data }: any, { dispatch }) => {
    try {
      dispatch(handleBackdropConfig({ open: true }));
      const resp: any = await cartService.updateDelvType({ body: data }); //postAPICall(nextAPIRoutes.UPDATE_DELV_TYPE, data)
      clearCartCache();
      dispatch(fetchCart({ query: "" }));
      dispatch(handleBackdropConfig({ open: false }));

      return resp?.data?.data ?? [];
    } catch (e: any) {
      dispatch(handleBackdropConfig({ open: false }));
      dispatch(
        handleSnackBar({
          open: true,
          severity: "error",
          message: e?.response?.data?.message ?? MESSAGES.ERROR,
        })
      );
      return { items: [] };
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    loadingCart: false,
    items: [],
    subtotal: 0,
    haystacker_fees: 0,
    security_deposit: 0,
    taxes: 0,
    total: 0,
    booking_type: 0,
    amount_due: 0,
    insurancePrice: 0,

    addingToCartId: 0,
    insurance: [],
    isDeleting: false,
  },
  reducers: {
    setLoadingCart: (state, { payload }: { payload: boolean }) => {
      state.loadingCart = payload;
    },
    setAddingToCartId: (state, { payload }: { payload: number }) => {
      state.addingToCartId = payload;
    },
    setIsDeleting: (state, { payload }: { payload: boolean }) => {
      state.isDeleting = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.fulfilled, (state, { payload }) => {
        state.items = payload?.items ?? [];
        state.subtotal = payload?.subtotal ?? 0;
        state.haystacker_fees = payload?.haystacker_fees ?? 0;
        state.security_deposit = payload?.security_deposit ?? 0;
        state.taxes = payload?.taxes ?? 0;
        state.total = payload?.total ?? 0;
        state.booking_type = payload?.booking_type ?? 0;
        state.insurancePrice = payload?.insurance ?? 0;
        state.amount_due = payload?.amount_due ?? 0;
      })
      .addCase(fetchInsurance.fulfilled, (state, { payload }) => {
        state.insurance = payload;
      });
  },
});

export default cartSlice.reducer;
export const { setLoadingCart, setAddingToCartId, setIsDeleting } =
  cartSlice.actions;
